import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { WebsiteNavbar } from './WebsiteNavbar';
import { WebsiteFooter } from './WebsiteFooter';
import { 
  ChevronRight, 
  Play, 
  Users, 
  Building2, 
  FileText, 
  MessageSquare, 
  Calendar, 
  Shield,
  Check,
  ArrowRight,
  Sparkles,
  Coffee,
  Clock,
  Award
} from 'lucide-react';

// Parallax Hook
const useParallax = (speed = 0.5) => {
  const [offset, setOffset] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setOffset(window.pageYOffset * speed);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);
  
  return offset;
};

// Stats Counter Animation
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration, isVisible]);
  
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

const WebsiteHome = () => {
  const parallaxOffset = useParallax(0.3);
  const parallaxOffsetSlow = useParallax(0.15);

  // Features data - Repris du site syndicappli.fr
  const features = [
    {
      icon: Calendar,
      title: 'AG en Visio à 360°',
      description: 'Vivez votre Assemblée Générale à 360° en totale immersion grâce à votre smartphone, PC, Mac ou tablette.',
    },
    {
      icon: FileText,
      title: 'VPC Digitalisés',
      description: 'Avec Syndicappli, votez par correspondance directement sur l\'appli en 2 clics.',
    },
    {
      icon: Shield,
      title: 'Vote Électronique À Distance',
      description: 'Participation et vote online sécurisé lors des AG en présence virtuelle authentifiée.',
    },
    {
      icon: MessageSquare,
      title: 'Messagerie Instantanée',
      description: 'Prise en main simple et intuitive pour faciliter la vie de vos coproprietaires.',
    },
    {
      icon: Building2,
      title: 'Déclaration / Suivi D\'incidents',
      description: 'Déclarez les incidents par l\'envoi d\'une simple photo/vidéo et suivez leur résolution en temps réel.',
    },
    {
      icon: Users,
      title: 'Feuille De Présence Digitalisée',
      description: 'Feuille de présence digitale avec signature électronique intégrée pour gagner du temps.',
    }
  ];

  // Pricing plans - Repris du site syndicappli.fr
  const plans = [
    {
      name: 'FREEMIUM',
      subtitle: '(Ambassadeurs)',
      price: '0',
      period: '€',
      description: 'pendant 1 mois',
      features: [
        'Création de 10 AG maximum',
        'Création multi-comptes jusqu\'à 2 gestionnaires gratuits',
        'Envoi LRE payant 3,00€ HT/envoi',
        'Visio simple uniquement'
      ],
      cta: 'Commencer gratuitement',
      popular: false
    },
    {
      name: 'PREMIUM',
      subtitle: '(à partir de 10 immeubles)',
      price: '2',
      period: '€',
      description: '/ mois / copro',
      features: [
        'Création de 50 AG par mois',
        'Création multi-comptes dont 1 Directeur copro et jusqu\'à 10 gestionnaires inclus',
        'Envoi LRE payant 2,50€ HT',
        'Visio simple et 360° au choix',
        'Pack 360° en location 149€ HT/mois',
        'Support client 24/7 par chatbot IA'
      ],
      cta: 'Essai gratuit',
      popular: true
    },
    {
      name: 'ILLIMITÉ',
      subtitle: '(à partir de 50 immeubles)',
      price: '4',
      period: '€',
      description: '/ mois / copro',
      features: [
        'Création d\'AG illimitée',
        'Création multi-comptes illimitée de Directeurs copro et gestionnaires inclus',
        'Envoi LRE payant 2,00€ HT',
        'Visio simple et 360° au choix',
        'Pack 360° à l\'achat 990€ HT',
        'Support client téléphonique et par chatbot IA 24/7'
      ],
      cta: 'Contacter les ventes',
      popular: false
    }
  ];

  // Partner logos - Logos du site syndicappli.fr
  const partners = [
    'https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Logos_Trampoleen_Site_250x80.png?resize=250%2C80&ssl=1',
    'https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Logos_LMT_Site_250x80.png?resize=250%2C80&ssl=1',
    'https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Logos_UP_Site_250x80.png?resize=250%2C80&ssl=1',
    'https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Logos_Idenergie_Site_250x80.png?resize=250%2C80&ssl=1',
    'https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Logos_LavalAgglo_Site_250x80.png?resize=250%2C80&ssl=1',
    'https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Logos_PaysDeLaLoire_Site_250x80.png?resize=250%2C80&ssl=1',
    'https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Logos_InitiativeMayenne_Site_250x80.png?resize=250%2C80&ssl=1',
  ];

  // Stats - Repris du site syndicappli.fr
  const stats = [
    { icon: Coffee, value: 7530, suffix: '', label: 'Cafés consommés' },
    { icon: Clock, value: 24, suffix: '', label: 'Mois de développement' },
    { icon: Users, value: 10, suffix: '', label: 'Ambassadeurs' },
    { icon: Award, value: 36, suffix: '', label: 'Mois d\'incubation' }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="website-home">
      <WebsiteNavbar />

      {/* Hero Section with Parallax */}
      <section className="relative min-h-screen overflow-hidden" style={{ background: 'linear-gradient(135deg, #00D3A8 0%, #00B894 50%, #009B7D 100%)' }}>
        {/* Parallax Background Elements */}
        <div 
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ transform: `translateY(${parallaxOffsetSlow}px)` }}
        >
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute top-40 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 left-1/3 w-64 h-64 bg-white rounded-full blur-3xl"></div>
        </div>

        {/* Floating geometric shapes with parallax */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{ transform: `translateY(${parallaxOffset}px)` }}
        >
          <div className="absolute top-32 left-[10%] w-4 h-4 bg-white/30 rounded-full animate-pulse"></div>
          <div className="absolute top-48 right-[15%] w-6 h-6 bg-white/20 rounded-lg rotate-45 animate-bounce" style={{ animationDuration: '3s' }}></div>
          <div className="absolute top-[60%] left-[5%] w-3 h-3 bg-white/40 rounded-full animate-ping" style={{ animationDuration: '2s' }}></div>
          <div className="absolute top-[40%] right-[8%] w-8 h-8 bg-white/10 rounded-full animate-pulse" style={{ animationDuration: '4s' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div 
              className="text-white"
              style={{ transform: `translateY(${parallaxOffset * 0.3}px)` }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full text-sm mb-8 border border-white/20">
                <Sparkles className="w-4 h-4" />
                <span>La meilleure appli de gestion d'AG hybrides</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                L'application qui gère vos
                <span className="block text-white/90">AG hybrides de A à Z</span>
              </h1>
              
              <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed max-w-xl">
                Notre application, ultra sécurisée et conforme RGPD vous permet de créer, 
                d'organiser, gérer et tenir toutes vos assemblées générales hybrides en quelques clics.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://calendly.com/syndicappli/demo_syndicappli"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-demo-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#00B894] rounded-full font-semibold hover:shadow-2xl hover:shadow-white/25 transition-all duration-300 group hover:scale-105"
                >
                  Prêt pour une démo ?
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
                <button
                  data-testid="hero-video-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 backdrop-blur-sm text-white rounded-full font-semibold hover:bg-white/25 transition-all duration-300 border border-white/20"
                  onClick={() => document.getElementById('video-section').scrollIntoView({ behavior: 'smooth' })}
                >
                  <Play className="w-5 h-5" />
                  Voir la démo vidéo
                </button>
              </div>

              {/* Trust badges */}
              <div className="mt-12 flex items-center gap-6">
                <div className="flex -space-x-3">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-white/25 border-2 border-white/50 flex items-center justify-center text-xs font-bold backdrop-blur-sm">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div className="text-sm">
                  <div className="font-semibold">10 Ambassadeurs actifs</div>
                  <div className="text-white/70">Syndics et gestionnaires</div>
                </div>
              </div>
            </div>

            {/* Right - Mockup avec images du site syndicappli.fr */}
            <div 
              className="relative lg:pl-12"
              style={{ transform: `translateY(${-parallaxOffset * 0.2}px)` }}
            >
              <div className="relative">
                {/* Main Phone Mockup - Image du site */}
                <div className="relative z-10 mx-auto">
                  <img 
                    src="https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Mockup_iOS_Syndicappli_copro_mobile_splashscreen.png?resize=575%2C1073&ssl=1"
                    alt="SyndicAppli Mobile"
                    className="w-64 md:w-72 h-auto mx-auto drop-shadow-2xl"
                  />
                </div>

                {/* Floating Cards with Parallax */}
                <div 
                  className="absolute -left-8 top-20 bg-white rounded-2xl shadow-xl p-4 animate-float"
                  style={{ transform: `translateY(${parallaxOffset * 0.5}px)` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#00D3A8]/10 flex items-center justify-center">
                      <Check className="w-5 h-5 text-[#00D3A8]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-700">AG votée</div>
                      <div className="text-xs text-slate-500">Résolution adoptée</div>
                    </div>
                  </div>
                </div>

                <div 
                  className="absolute -right-4 bottom-32 bg-white rounded-2xl shadow-xl p-4 animate-float-delayed"
                  style={{ transform: `translateY(${parallaxOffset * 0.3}px)` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-700">Nouveau message</div>
                      <div className="text-xs text-slate-500">De votre gestionnaire</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-white" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-8">Ils nous soutiennent</p>
          <div className="flex flex-wrap items-center justify-center gap-12">
            {partners.map((logo, index) => (
              <img
                key={index}
                src={logo}
                alt="Partner"
                className="h-12 object-contain opacity-40 hover:opacity-70 transition-all duration-300"
                style={{ filter: 'grayscale(100%)' }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section with Parallax */}
      <section className="py-24 bg-slate-50 relative overflow-hidden" data-testid="features-section">
        {/* Background Parallax */}
        <div 
          className="absolute top-0 right-0 w-96 h-96 bg-[#00D3A8]/5 rounded-full blur-3xl"
          style={{ transform: `translateY(${parallaxOffset * 0.2}px)` }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Une suite complète d'outils pour gérer efficacement vos copropriétés au quotidien.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-card-${index}`}
                className="group bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-xl hover:border-[#00D3A8]/30 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#00D3A8]/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-[#00D3A8]/20 transition-all">
                  <feature.icon className="w-7 h-7 text-[#00D3A8]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3">{feature.title}</h3>
                <p className="text-slate-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section with Parallax */}
      <section 
        className="py-24 relative overflow-hidden" 
        style={{ background: 'linear-gradient(135deg, #00D3A8 0%, #00B894 100%)' }}
        data-testid="stats-section"
      >
        {/* Parallax Background */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{ transform: `translateY(${parallaxOffset * 0.1}px)` }}
        >
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-2xl"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full blur-2xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <stat.icon className="w-10 h-10 mx-auto mb-4 opacity-80" />
                <div className="text-4xl md:text-5xl font-bold mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="py-24 bg-white" id="video-section" data-testid="video-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Découvrez pourquoi notre application est une révolution
            </h2>
            <p className="text-lg text-slate-500 max-w-3xl mx-auto">
              Nous avons conçu notre application en étroite collaboration avec les acteurs du métier. 
              Ce process de production "rapproché" a permis d'augmenter grandement l'efficacité de notre 
              solution et de répondre aux attentes clients.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="aspect-video rounded-2xl overflow-hidden bg-slate-100 shadow-2xl">
              <video
                controls
                poster="https://i0.wp.com/www.syndicappli.fr/wp-content/uploads/2023/04/Mockup_AppleiPhone12ProMaxSilver_iOS_Syndicappli_copro_SMALL6-1.gif?w=750&ssl=1"
                className="w-full h-full object-cover"
              >
                <source src="https://www.syndicappli.fr/wp-content/uploads/videos/VIdeo_Syndicappli_HD1080.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            </div>
            <p className="text-center mt-6 text-slate-500">
              Cliquez pour découvrir comment Syndicappli digitalise vos Assemblées générales
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-slate-50" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nos offres
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Nous proposons une tarification adaptée en fonction de votre portefeuille. 
              Nous pouvons personnaliser nos offres afin de l'adapter à votre structure.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                className={`relative bg-white rounded-3xl p-8 transition-all duration-300 hover:shadow-xl ${
                  plan.popular
                    ? 'ring-2 ring-[#00D3A8] shadow-xl scale-105'
                    : 'border border-slate-200 shadow-sm hover:border-[#00D3A8]/30'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#00D3A8] text-white text-sm font-semibold rounded-full">
                    POUR VOUS
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-xl font-bold text-slate-800 mb-0">{plan.name}</h3>
                  <p className="text-slate-500 text-sm mb-4">{plan.subtitle}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-slate-800">{plan.price}</span>
                    <span className="text-2xl font-bold text-slate-800">{plan.period}</span>
                  </div>
                  <p className="text-slate-500 text-sm mt-1">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <span className="text-[#00D3A8] mt-0.5">⯈</span>
                      <span className="text-slate-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://calendly.com/syndicappli/demo_syndicappli"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full py-3 rounded-full font-semibold text-center transition-all duration-300 ${
                    plan.popular
                      ? 'bg-[#00D3A8] text-white hover:bg-[#00B894] hover:shadow-lg hover:shadow-[#00D3A8]/30'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden" data-testid="cta-section">
        {/* Parallax decorations */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{ transform: `translateY(${parallaxOffset * 0.1}px)` }}
        >
          <div className="absolute top-10 left-[20%] w-64 h-64 bg-[#00D3A8] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-[20%] w-80 h-80 bg-[#00D3A8] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Prêt à simplifier votre gestion de copropriété ?
          </h2>
          <p className="text-xl text-slate-400 mb-10">
            Rejoignez plus de 500 syndics qui nous font déjà confiance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/site/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00D3A8] text-white rounded-full font-semibold hover:bg-[#00B894] hover:shadow-lg hover:shadow-[#00D3A8]/30 transition-all"
            >
              Réserver une démo gratuite
              <ChevronRight className="w-5 h-5" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white rounded-full font-semibold hover:bg-white/20 transition-all"
            >
              Se connecter
            </Link>
          </div>
        </div>
      </section>

      <WebsiteFooter />

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 3s ease-in-out infinite;
          animation-delay: 1.5s;
        }
      `}</style>
    </div>
  );
};

export { WebsiteHome };
